import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	Typography,
	Alert,
	Button,
} from '@mui/material';
import { ContentCopyRounded, CheckRounded, RefreshRounded } from '@mui/icons-material';
import { apiErrorMessage } from '../../../api/catalog';
import { IDocenteMateria } from '../../../types/miRecurso';
import { CodeDisplayBox, BigCodeText, CopyButton, ModalActions } from './styles';

interface CodigoMateriaDialogProps {
	open: boolean;
	materia: IDocenteMateria | null;
	onClose: () => void;
	onRegenerarCodigo: (materiaId: number) => Promise<void>;
}

const CodigoMateriaDialog: React.FC<CodigoMateriaDialogProps> = ({
	open,
	materia,
	onClose,
	onRegenerarCodigo,
}) => {
	const [copied, setCopied] = useState(false);
	const [confirmingRegenerar, setConfirmingRegenerar] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleCopy = () => {
		if (!materia) return;
		navigator.clipboard.writeText(materia.codigo).catch(() => {});
		setCopied(true);
		setTimeout(() => setCopied(false), 2500);
	};

	const handleClose = () => {
		setCopied(false);
		setConfirmingRegenerar(false);
		onClose();
	};

	const handleRegenerarClick = () => {
		setConfirmingRegenerar(true);
	};

	const handleConfirmRegenerar = async () => {
		if (!materia || saving) return;
		setSaving(true);
		setError(null);
		try {
			await onRegenerarCodigo(materia.id);
			setConfirmingRegenerar(false);
			setCopied(false);
			setSaving(false);
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleCancelRegenerar = () => {
		setConfirmingRegenerar(false);
	};

	if (!materia) return null;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>Código de materia</DialogTitle>

			<DialogContent>
				<DialogContentText gutterBottom>
					<strong>{materia.nombre}</strong>
				</DialogContentText>

				<CodeDisplayBox>
					<BigCodeText>{materia.codigo}</BigCodeText>
					<CopyButton
						variant={copied ? 'contained' : 'outlined'}
						color={copied ? 'success' : 'primary'}
						onClick={handleCopy}
						startIcon={copied ? <CheckRounded /> : <ContentCopyRounded />}
					>
						{copied ? '¡Copiado!' : 'Copiar'}
					</CopyButton>
				</CodeDisplayBox>

				{confirmingRegenerar ? (
					<Alert severity='warning' variant='outlined'>
						<Typography variant='body2' gutterBottom>
							<strong>¿Regenerar el código?</strong> El código actual dejará de funcionar
							y los alumnos que aún no se hayan vinculado necesitarán el nuevo código.
						</Typography>
						<Button
							size='small'
							color='warning'
							variant='contained'
							onClick={handleConfirmRegenerar}
							disabled={saving}
							style={{ marginRight: 8 }}
						>
							Sí, regenerar
						</Button>
						<Button size='small' onClick={handleCancelRegenerar} disabled={saving}>
							Cancelar
						</Button>
					</Alert>
				) : (
					<Alert severity='info' variant='outlined'>
						<Typography variant='body2'>
							Comparte este código con tus alumnos para que puedan vincularse a tu materia y
							acceder a los recursos que has subido.
						</Typography>
					</Alert>
				)}
				{error && <Alert severity='error'>{error}</Alert>}
			</DialogContent>

			<ModalActions>
				<Button
					startIcon={<RefreshRounded />}
					onClick={handleRegenerarClick}
					disabled={confirmingRegenerar}
					color='warning'
				>
					Regenerar código
				</Button>
				<Button variant='contained' onClick={handleClose}>
					Listo
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default CodigoMateriaDialog;
